import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useLang } from '../context/LangContext';
import { useAuth } from '../hooks/useAuth';
import Header        from '../components/Header';
import ChapterView   from '../components/learning/ChapterView';
import GlossaryTable from '../components/learning/GlossaryTable';
import GapWorksheet  from '../components/learning/GapWorksheet';
import ProgressBar   from '../components/learning/ProgressBar';
import '../components/learning/learning.css';
import { meta, parts, chaptersIndex, chapters, TOTAL_EXERCISES } from '../content/learning';
import { getLocalized } from '../content/learning/getLocalized';

const NAVY = '#0d1b3e';
const GOLD = '#c9a23a';

const TABS = [
  { id: 'chapter',  labelKey: 'tabChapters' },
  { id: 'glossary', labelKey: 'tabGlossary' },
  { id: 'gap',      labelKey: 'tabGap'      },
];

/* Gold scalloped wave separator */
function ScallopWave() {
  const CREAM = '#f0e5d0';
  const archW = 48;
  const total = 30;
  const W = archW * total; // 1440
  const H = 50;
  const base = 28;

  let d = `M0,${H} L0,${base} `;
  for (let i = 0; i < total; i++) {
    const cx = i * archW + archW / 2;
    const ex = (i + 1) * archW;
    d += `Q${cx},0 ${ex},${base} `;
  }
  d += `L${W},${H} Z`;

  return (
    <div style={{ background: NAVY, lineHeight: 0 }}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        style={{ display: 'block', width: '100%', height: H }}
      >
        <rect width={W} height={H} fill={CREAM} />
        <path d={d} fill={GOLD} />
      </svg>
    </div>
  );
}

export default function LearningCenterPage() {
  const { t, dir, lang } = useLang();
  const { user, token } = useAuth();
  const L = t.learning || {};

  const [selectedId,     setSelectedId]     = useState('ch01');
  const [activeTab,      setActiveTab]       = useState('chapter');
  const [completedItems, setCompletedItems]  = useState(new Set());
  const [sidebarOpen,    setSidebarOpen]     = useState(false);
  const [expandedParts,  setExpandedParts]   = useState(() => {
    const ch = chaptersIndex.find((c) => c.id === 'ch01');
    return new Set([ch?.part ?? 1]);
  });

  const chapter = useMemo(() => chapters[selectedId], [selectedId]);

  /* Fetch progress on mount */
  useEffect(() => {
    if (!user || !token) return;
    axios
      .get('/api/learning/progress', { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setCompletedItems(new Set(r.data.completed)))
      .catch(() => {});
  }, [user, token]);

  /* Toggle exercise — optimistic UI */
  const handleToggle = useCallback(
    async (itemId, shouldComplete) => {
      if (!user) return;
      setCompletedItems((prev) => {
        const next = new Set(prev);
        if (shouldComplete) next.add(itemId); else next.delete(itemId);
        return next;
      });
      try {
        if (shouldComplete) {
          await axios.post(`/api/learning/progress/${itemId}`, {}, { headers: { Authorization: `Bearer ${token}` } });
        } else {
          await axios.delete(`/api/learning/progress/${itemId}`, { headers: { Authorization: `Bearer ${token}` } });
        }
      } catch {
        setCompletedItems((prev) => {
          const next = new Set(prev);
          if (shouldComplete) next.delete(itemId); else next.add(itemId);
          return next;
        });
      }
    },
    [user, token]
  );

  const selectChapter = (id) => {
    setSelectedId(id);
    setActiveTab('chapter');
    setSidebarOpen(false);
    // auto-expand the part containing this chapter
    const ch = chaptersIndex.find((c) => c.id === id);
    if (ch) setExpandedParts((prev) => new Set([...prev, ch.part]));
  };

  const togglePart = (num) =>
    setExpandedParts((prev) => {
      const next = new Set(prev);
      if (next.has(num)) next.delete(num); else next.add(num);
      return next;
    });

  /* Count done per chapter for badge */
  const chapterDone = useMemo(() => {
    const map = {};
    for (const item of completedItems) {
      const match = item.match(/^(ch\d+)-ex-\d+$/);
      if (match) map[match[1]] = (map[match[1]] || 0) + 1;
    }
    return map;
  }, [completedItems]);

  return (
    <div style={{ fontFamily: 'Tajawal, sans-serif' }}>
      <Header />

      {/* ── HERO ──────────────────────────────────────────────── */}
      <div className="lc-hero" dir={dir}>
        <p className="lc-hero-eyebrow">RESEARCH METHODOLOGY GUIDE</p>
        <h1 className="lc-hero-title">{getLocalized(meta.title, lang)}: {getLocalized(meta.subtitle, lang)}</h1>
        {lang !== 'en' && (
          <p className="lc-hero-subtitle-en">
            {meta.title?.en} — {meta.subtitle?.en}
          </p>
        )}
        <p className="lc-hero-desc">{L.heroDesc}</p>
        <div className="lc-hero-badges">
          <span className="lc-hero-badge">{meta.totalChapters} {L.badgeUnits}</span>
          <span className="lc-hero-badge" dir="ltr">عربي · English · فارسی</span>
          <span className="lc-hero-badge">{L.badgeInteractive}</span>
        </div>
      </div>

      {/* Gold scallop wave */}
      <ScallopWave />

      {/* ── BODY ──────────────────────────────────────────────── */}
      <div className="lc-page">

        {/* Mobile overlay */}
        <div
          className={`lc-overlay${sidebarOpen ? ' open' : ''}`}
          onClick={() => setSidebarOpen(false)}
        />

        <div className="lc-body" dir={dir}>

          {/* ── Sidebar TOC (first = right in RTL) ── */}
          <aside className={`lc-sidebar${sidebarOpen ? ' open' : ''}`}>

            <div className="lc-toc-head">{L.tocTitle}</div>

            {parts.map((part) => {
              const partChapters = chaptersIndex.filter((c) => c.part === part.num);
              const isOpen = expandedParts.has(part.num);
              const hasActive = partChapters.some((c) => c.id === selectedId);
              return (
                <div key={part.id}>
                  {/* Part header — always visible, clickable to expand/collapse */}
                  <button
                    onClick={() => togglePart(part.num)}
                    style={{
                      width: '100%', display: 'flex', alignItems: 'center', gap: 8,
                      padding: '9px 16px',
                      background: hasActive ? 'rgba(201,162,58,0.08)' : 'none',
                      border: 'none',
                      borderTop: part.num === 1 ? 'none' : '1px solid rgba(201,162,58,0.15)',
                      cursor: 'pointer', textAlign: 'start',
                      fontFamily: 'Tajawal, sans-serif',
                    }}
                  >
                    <span style={{
                      fontSize: 11, fontWeight: 700, color: GOLD,
                      flexShrink: 0, minWidth: 16,
                    }}>
                      {part.num}
                    </span>
                    <span style={{
                      fontSize: 12, fontWeight: 700,
                      color: hasActive ? NAVY : '#7a5c2e',
                      flex: 1, lineHeight: 1.4, textAlign: 'start',
                    }}>
                      {getLocalized(part.title, lang)}
                    </span>
                    <span style={{
                      fontSize: 9, color: GOLD, flexShrink: 0,
                      display: 'inline-block',
                      transform: isOpen ? 'rotate(90deg)' : 'none',
                      transition: 'transform 0.2s',
                    }}>▶</span>
                  </button>

                  {/* Chapter list — shown only when part is expanded */}
                  {isOpen && partChapters.map((ch) => {
                    const done  = chapterDone[ch.id] || 0;
                    const total = chapters[ch.id]?.exercise?.length || 0;
                    const active = selectedId === ch.id && activeTab === 'chapter';
                    return (
                      <button
                        key={ch.id}
                        className={`lc-toc-btn${active ? ' active' : ''}`}
                        onClick={() => selectChapter(ch.id)}
                      >
                        <span className="lc-toc-num">{String(ch.num).padStart(2, '0')}</span>
                        <span className="lc-toc-label" style={{ flex: 1 }}>{getLocalized(ch.title, lang)}</span>
                        {total > 0 && (
                          <span style={{
                            fontSize: 10, flexShrink: 0, marginInlineStart: 4,
                            color: done === total ? GOLD : '#a08060',
                          }}>
                            {done}/{total}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              );
            })}

            <div className="lc-toc-divider" />

            <button
              className={`lc-toc-extra${activeTab === 'glossary' ? ' active' : ''}`}
              onClick={() => { setActiveTab('glossary'); setSidebarOpen(false); }}
            >
              📖 {L.tabGlossary}
            </button>
            <button
              className={`lc-toc-extra${activeTab === 'gap' ? ' active' : ''}`}
              onClick={() => { setActiveTab('gap'); setSidebarOpen(false); }}
            >
              ✏️ {L.tabGap}
            </button>

            {user ? (
              <div style={{ padding: '12px 16px', borderTop: '1px solid rgba(201,162,58,0.18)', marginTop: 6 }}>
                <ProgressBar completed={completedItems.size} total={TOTAL_EXERCISES} />
              </div>
            ) : (
              <p style={{ margin: 0, padding: '12px 16px', fontSize: 12, color: '#7a6040', lineHeight: 1.6, borderTop: '1px solid rgba(201,162,58,0.18)', marginTop: 6, textAlign: 'center' }}>
                <a href="/login" style={{ color: GOLD, textDecoration: 'none' }}>{L.loginPrompt}</a>
              </p>
            )}
          </aside>

          {/* ── Main content ── */}
          <main className="lc-main">

            {/* How-to card */}
            <div className="lc-how-card">
              <strong style={{ color: NAVY }}>{L.howToUseTitle}</strong>{' '}
              {L.howToUse}
            </div>

            {/* Tab bar */}
            <div className="lc-tabs">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  className={`lc-tab-btn${activeTab === tab.id ? ' active' : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {L[tab.labelKey]}
                </button>
              ))}
            </div>

            {/* Content */}
            <div className="lc-content-card">
              {activeTab === 'chapter' && (
                <ChapterView
                  chapter={chapter}
                  completedItems={completedItems}
                  onToggle={handleToggle}
                />
              )}
              {activeTab === 'glossary' && <GlossaryTable />}
              {activeTab === 'gap'      && <GapWorksheet  />}
            </div>
          </main>
        </div>
      </div>

      {/* Mobile FAB */}
      <button
        className="lc-fab"
        onClick={() => setSidebarOpen(true)}
        aria-label="open chapters"
      >
        ☰
      </button>
    </div>
  );
}
